import { useCallback } from 'react';
import type { GpuBudgetListItem } from '../types/models/GpuBudget';
import type { ApiResponseGpuBudgets } from '../types/api/ApiResponseGpuBudget';
import type { GpuJsendEnvelope } from './gpuServerResponse';
import { useGpuBudgetFetchOnce } from './useGpuBudgetFetchOnce';

export interface UseGpuBudgetsReturn {
  /** `null` until the first successful read. */
  budgets: GpuBudgetListItem[] | null;
  loading: boolean;
  error: string | null;
  /** Background re-read (the current list stays on screen). */
  refetch: () => Promise<void>;
  /** Put a budget a write returned into the list at once (replaced in place, or added first). */
  upsert: (item: GpuBudgetListItem) => void;
}

const pickBudgets = (body: GpuJsendEnvelope): GpuBudgetListItem[] | null => {
  const budgets = (body as ApiResponseGpuBudgets).data?.budgets;
  return Array.isArray(budgets) ? budgets : null;
};

/**
 * `GET /datacenters/gpu-servers/budgets` — the organisation's budgets, most recently edited first, with
 * no pricing (docs/api-types/gpu-budgets.md). Read once when enabled (the Budget tab's mount), then
 * only on `refetch` — after a write (its `line_count` / order changed) or on Refresh. Never polled.
 */
export const useGpuBudgets = (enabled: boolean = true): UseGpuBudgetsReturn => {
  const { data, loading, error, refetch, setData } = useGpuBudgetFetchOnce(
    '/datacenters/gpu-servers/budgets',
    pickBudgets,
    'Could not load your budgets.',
    enabled,
  );

  const upsert = useCallback(
    (item: GpuBudgetListItem) =>
      setData((current) => {
        const list = current ?? [];
        return list.some((budget) => budget.id === item.id)
          ? list.map((budget) => (budget.id === item.id ? item : budget))
          : [item, ...list];
      }),
    [setData],
  );

  return { budgets: data, loading, error, refetch, upsert };
};

export default useGpuBudgets;
